import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CACHE_KEY = "menu_config_cache";

const subscribers = new Set();

const store = {
  menuConfig: null,
  loading: true,
  error: null,
  initialized: false,
  inflight: null,
};

const notifySubscribers = () => {
  const snapshot = {
    menuConfig: store.menuConfig,
    loading: store.loading,
    error: store.error,
  };

  subscribers.forEach((listener) => listener(snapshot));
};

const readCachedMenuConfig = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    const parsed = JSON.parse(cached);
    return parsed?.items ? parsed : null;
  } catch {
    return null;
  }
};

const persistMenuConfig = (data) => {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ version: data.version, items: data.items })
    );
  } catch {
  }
};

const hydrateFromCache = () => {
  const cached = readCachedMenuConfig();
  if (cached) {
    store.menuConfig = cached;
    store.loading = false;
  }
  return cached;
};

const fetchSharedMenuConfig = async ({ force = false } = {}) => {
  if (store.inflight && !force) {
    return store.inflight;
  }

  if (!force && store.initialized) {
    return store.menuConfig;
  }

  store.loading = true;
  if (force) {
    store.error = null;
  }
  notifySubscribers();

  store.inflight = axios
    .get(`${API}/menu/config`)
    .then((response) => {
      const data = response.data;

      if (data?.items) {
        store.menuConfig = data;
        persistMenuConfig(data);
      }

      store.error = null;
      store.initialized = true;
      return store.menuConfig;
    })
    .catch((err) => {
      if (!store.menuConfig) {
        store.menuConfig = readCachedMenuConfig();
      }
      store.error = err;
      store.initialized = true;
      return store.menuConfig;
    })
    .finally(() => {
      store.loading = false;
      store.inflight = null;
      notifySubscribers();
    });

  return store.inflight;
};

export default function useMenuConfig() {
  const [state, setState] = useState(() => {
    if (!store.initialized && !store.menuConfig) {
      hydrateFromCache();
    }

    return {
      menuConfig: store.menuConfig,
      loading: store.loading,
      error: store.error,
    };
  });

  useEffect(() => {
    subscribers.add(setState);
    return () => subscribers.delete(setState);
  }, []);

  useEffect(() => {
    if (!store.initialized) {
      fetchSharedMenuConfig();
    }
  }, []);

  const refetch = useCallback(() => fetchSharedMenuConfig({ force: true }), []);

  return { ...state, refetch };
}
